import { BorderedContainer } from 'core/components/common/BorderedContainer';
import { IncidentDetailPatientForSendai } from 'sections/sendai/features/useIncidentDetailForSendai';
import { SectionTitle } from 'core/components/common/SectionTitle';
import { InfoDisplay } from 'core/components/JianDetail/common/InfoDisplay';
import { TagList } from 'core/components/JianDetail/PatientDetailInfo/common/TagList';
import { convertToPresenceAbsence } from 'core/components/common/converters';
import { FC } from 'react';

type Props = {
  /** 要請概要。 */
  requestSummary: IncidentDetailPatientForSendai['requestSummary'];
  /** 同乗者リスト (基本情報から取得) */
  accompanyingList?: IncidentDetailPatientForSendai['baseInfo']['accompanyingList'];
};

export const RequestSummaryView: FC<Props> = ({
  requestSummary,
  accompanyingList,
}) => {
  if (!requestSummary) {
    return (
      <BorderedContainer>
        <div className="flex flex-col gap-[24px]">
          <SectionTitle text="要請概要欄" />
          <p className="text-gray-500">データが存在しません</p>
        </div>
      </BorderedContainer>
    );
  }

  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="要請概要欄" />
        <div className="flex flex-col gap-6">
          <InfoDisplay
            title="救急要請の概要(発生原因)"
            content={requestSummary.onsetSummary ?? ''}
          />
          <InfoDisplay
            title="現場到着・傷病者接触時の状況(主訴)"
            content={requestSummary.siteArrivalSituationSummary ?? ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="既往の有無"
            content={convertToPresenceAbsence(requestSummary.isMedicalHistory)}
          />
        </div>
        <div className="flex flex-col gap-6">
          <InfoDisplay
            title="既往等"
            content={<TagList tags={requestSummary.medicalHistory ?? []} />}
          />
          <InfoDisplay
            title="既往等 自由入力欄"
            content={requestSummary.medicalHistoryComment ?? ''}
          />
        </div>
        <div className="flex flex-col gap-6">
          <InfoDisplay
            title="服薬状況"
            content={
              <TagList
                tags={
                  requestSummary.medicationStatus?.map(
                    status => status.medicationName ?? ''
                  ) ?? []
                }
              />
            }
          />
          <InfoDisplay
            title="服薬状況 自由入力欄"
            content={requestSummary.medicationStatusComment ?? ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="アレルギー"
            content={requestSummary.allergies ?? ''}
          />
          <InfoDisplay
            title="最終食事"
            content={requestSummary.lastMealComment ?? ''}
          />
          <InfoDisplay
            title="飲酒内容"
            content={requestSummary.drunkText ?? ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="ADL 歩行" content={requestSummary.adl ?? ''} />
          <InfoDisplay
            title="ADL 排泄"
            content={requestSummary.adlExcretion ?? ''}
          />
          <InfoDisplay
            title="ADL 意思疎通"
            content={requestSummary.adlCommunication ?? ''}
          />
        </div>
        {/* 同乗者情報 */}
        {accompanyingList && accompanyingList.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-x-[24px] gap-y-[24px]">
            {accompanyingList.map((person, index) => (
              <InfoDisplay
                key={index}
                title={`同乗者(${index + 1}人目)`}
                content={person.accompanying ?? ''}
              />
            ))}
          </div>
        )}
      </div>
    </BorderedContainer>
  );
};
